import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const comic = useMemo(() => comics.find((c) => c.slug === id), [id]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter(
        (c) =>
          c.id !== comic.id &&
          c.categories.some((cat) => comic.categories.includes(cat))
      )
      .slice(0, 5);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">
              漫画未找到
            </h1>
            <p className="text-sm text-foreground-400 mb-6">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryNames = comic.categories
    .map((slug) => categories.find((c) => c.slug === slug)?.name)
    .filter(Boolean);

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} | 韩漫网免费在线漫画`}
        description={`韩漫网免费在线阅读《${comic.title}》，作者${comic.author}。${comic.description.slice(0, 120)}。高清韩国漫画，完全免费。`}
        keywords={`${comic.title},${comic.author},韩国漫画,在线漫画,韩漫网,${categoryNames.join(',')}漫画`}
        canonicalPath={`/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        articleData={{
          publishedTime: comic.chapters[0]?.updateDate,
          modifiedTime: comic.lastUpdated,
          author: comic.author,
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', href: '/' },
                { name: '分类浏览', href: '/category/all' },
                ...(comic.categories[0]
                  ? [{ name: categories.find((c) => c.slug === comic.categories[0])?.name || '', href: `/category/${comic.categories[0]}` }]
                  : []),
                { name: comic.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <article className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0 w-full md:w-[280px]">
              <div className="rounded-lg overflow-hidden aspect-[3/4] bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩国漫画封面`}
                  title={`${comic.title} - 韩漫网免费在线漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="text-sm text-foreground-500">
                  作者：<strong>{comic.author}</strong>
                </span>
                <span className="text-sm text-foreground-300">|</span>
                <span className="flex items-center text-sm text-foreground-500">
                  <i className="ri-star-fill text-accent-500 mr-1"></i>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="text-sm text-foreground-300">|</span>
                <span className="text-sm text-foreground-500">
                  <strong>{(comic.views / 10000).toFixed(1)}万</strong> 次阅读
                </span>
                <span className="text-sm text-foreground-300">|</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {categoryNames.map((name, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {name}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-lg bg-background-100">
                <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-500 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-4 flex items-center gap-3 text-xs text-foreground-400">
                <span>最近更新：{comic.lastUpdated}</span>
                <span>共 {comic.chapters.length} 话</span>
              </div>
            </div>
          </article>

          <section className="mt-12">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              章节列表
            </h2>
            <div className="bg-background-50 rounded-lg border border-background-200/70 divide-y divide-background-200/50 max-h-[500px] overflow-y-auto">
              {comic.chapters.map((chapter, index) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="text-sm text-foreground-700">{chapter.title}</span>
                  <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                </div>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading font-bold text-foreground-950">
                  相关推荐
                </h2>
                <Link
                  to="/category/all"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  查看更多
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}